/**
 * Presentation boundary for cat animation.
 *
 * QuadrupedLocomotion produces a renderer-free pose. A driver maps that pose to
 * the current visual, whether it is today's primitive rig or a future imported
 * skeleton. Authored clip blending can therefore arrive later without changing
 * CatController or any gameplay code.
 */

import * as THREE from 'three';
import { LEG_ORDER, type LegName, type QuadrupedPose } from '@/cat/quadrupedLocomotion';
import type { RagdollLegPose, RagdollVisualPose } from '@/cat/ragdoll';

export interface AnimationDriver {
  applyPose(pose: QuadrupedPose): void;
  applyRagdollPose?(pose: RagdollVisualPose): void;
}

export interface LegRigBinding {
  upper: THREE.Object3D;
  lower: THREE.Object3D;
  paw: THREE.Object3D;
}

export interface ProceduralRigBindings {
  /** Pose coordinates are expressed in this object's local space. */
  root: THREE.Object3D;
  spine: THREE.Object3D;
  head: THREE.Object3D;
  legs: Record<LegName, LegRigBinding>;
  tail: THREE.Object3D[];
  ears: [THREE.Object3D, THREE.Object3D];
}

const DOWN = new THREE.Vector3(0, -1, 0);

/** Applies procedural poses to a rig made of transform nodes. */
export class ProceduralAnimationDriver implements AnimationDriver {
  private readonly headRestPosition: THREE.Vector3;
  private readonly spineRestRotation: THREE.Quaternion;
  private readonly headRestRotation: THREE.Quaternion;
  private readonly tailRestRotations: THREE.Quaternion[];
  private readonly earRestRotations: [THREE.Quaternion, THREE.Quaternion];

  constructor(private readonly rig: ProceduralRigBindings) {
    this.headRestPosition = rig.head.position.clone();
    this.spineRestRotation = rig.spine.quaternion.clone();
    this.headRestRotation = rig.head.quaternion.clone();
    this.tailRestRotations = rig.tail.map((joint) => joint.quaternion.clone());
    this.earRestRotations = [rig.ears[0].quaternion.clone(), rig.ears[1].quaternion.clone()];
  }

  applyPose(pose: QuadrupedPose): void {
    applyRotationOffset(this.rig.spine, this.spineRestRotation, pose.spinePitch, 0, pose.spineRoll);

    this.rig.head.position.copy(this.headRestPosition);
    applyRotationOffset(this.rig.head, this.headRestRotation, pose.headPitch, pose.headYaw, 0);

    this.rig.root.updateWorldMatrix(true, true);

    for (const legName of LEG_ORDER) this.applyLeg(this.rig.legs[legName], pose.legs[legName]);

    for (let index = 0; index < this.rig.tail.length; index++) {
      const joint = this.rig.tail[index];
      const rest = this.tailRestRotations[index];
      if (joint && rest) applyRotationOffset(joint, rest, 0, pose.tail[index] ?? 0, 0);
    }

    applyRotationOffset(this.rig.ears[0], this.earRestRotations[0], 0, 0, pose.ears[0]);
    applyRotationOffset(this.rig.ears[1], this.earRestRotations[1], 0, 0, pose.ears[1]);
  }

  applyRagdollPose(pose: RagdollVisualPose): void {
    this.rig.spine.quaternion.copy(this.spineRestRotation);
    this.rig.root.updateWorldMatrix(true, true);
    setPositionInRigSpace(this.rig.root, this.rig.head, pose.head.position);
    setRotationInRigSpace(this.rig.root, this.rig.head, pose.head.rotation);

    for (const legName of LEG_ORDER) {
      this.applyLeg(this.rig.legs[legName], pose.legs[legName]);
    }
  }

  private applyLeg(binding: LegRigBinding, pose: RagdollLegPose): void {
    orientSegment(this.rig.root, binding.upper, pose.root, pose.knee);
    orientSegment(this.rig.root, binding.lower, pose.knee, pose.foot);
    setPositionInRigSpace(this.rig.root, binding.paw, pose.foot);
  }
}

function orientSegment(
  root: THREE.Object3D,
  segment: THREE.Object3D,
  start: { x: number; y: number; z: number },
  end: { x: number; y: number; z: number },
): void {
  const localStart = pointInParentSpace(root, segment, start);
  const localEnd = pointInParentSpace(root, segment, end);
  segment.position.copy(localStart);
  const direction = localEnd.sub(localStart);
  if (direction.lengthSq() === 0) {
    segment.quaternion.identity();
    return;
  }
  segment.quaternion.setFromUnitVectors(DOWN, direction.normalize());
  segment.updateWorldMatrix(false, true);
}

function setPositionInRigSpace(
  root: THREE.Object3D,
  object: THREE.Object3D,
  position: { x: number; y: number; z: number },
): void {
  object.position.copy(pointInParentSpace(root, object, position));
  object.updateWorldMatrix(false, true);
}

function pointInParentSpace(
  root: THREE.Object3D,
  object: THREE.Object3D,
  point: { x: number; y: number; z: number },
): THREE.Vector3 {
  const result = new THREE.Vector3(point.x, point.y, point.z);
  root.localToWorld(result);
  object.parent?.worldToLocal(result);
  return result;
}

function setRotationInRigSpace(
  root: THREE.Object3D,
  object: THREE.Object3D,
  rotation: { x: number; y: number; z: number; w: number },
): void {
  const rootWorld = root.getWorldQuaternion(new THREE.Quaternion());
  const desiredWorld = rootWorld.multiply(
    new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w),
  );
  const parentWorld = object.parent?.getWorldQuaternion(new THREE.Quaternion());
  object.quaternion.copy(parentWorld ? parentWorld.invert().multiply(desiredWorld) : desiredWorld);
  object.updateWorldMatrix(false, true);
}

function applyRotationOffset(
  object: THREE.Object3D,
  rest: THREE.Quaternion,
  x: number,
  y: number,
  z: number,
): void {
  const offset = new THREE.Quaternion().setFromEuler(new THREE.Euler(x, y, z, 'YXZ'));
  object.quaternion.copy(rest).multiply(offset);
}
